import { useEffect, useState, useCallback, useRef } from "react";
import { CardSkeleton } from "../components/Skeletons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Plus, Loader2, ArrowLeft, Save, Eye, Copy, Trash2,
  Pencil, FileSignature, FileText, Tag, ShieldCheck, Upload,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { BlockPalette, TagsPanel, EditableBlock, BlockRenderer, THEMES } from "./formation/BlockEditor";

// Types systeme (les types lies aux evaluations ont ete retires en iter_135).
const SYSTEM_TYPES = [
  { v: "contrat", l: "Contrat de formation", icon: FileSignature, ind: [22, 23] },
  { v: "convention", l: "Convention de formation", icon: FileSignature, ind: [22] },
  { v: "attestation", l: "Attestation de fin", icon: FileSignature, ind: [11] },
  { v: "certificat_realisation", l: "Certificat de realisation", icon: ShieldCheck, ind: [11, 12] },
  { v: "emargement", l: "Feuille d'emargement", icon: FileText, ind: [10] },
  { v: "programme", l: "Programme de formation", icon: FileText, ind: [1, 4] },
  { v: "reglement_interieur", l: "Reglement interieur", icon: FileText, ind: [9] },
  { v: "livret_accueil", l: "Livret d'accueil", icon: FileText, ind: [9] },
  { v: "autre", l: "Autre document", icon: FileText, ind: [] },
];

/* ═══════════════════════════════════════════════════════════════
 * List view
 * ═══════════════════════════════════════════════════════════════ */
export function DocTemplatesListPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [tpls, setTpls] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showNewType, setShowNewType] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [newTypeDesc, setNewTypeDesc] = useState("");
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const [importType, setImportType] = useState("autre");

  const DOC_TYPES = [
    ...SYSTEM_TYPES,
    ...customTypes.map((c) => ({ v: c.key, l: c.label, icon: FileText, ind: c.linked_indicators || [], custom: true })),
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tplsR, typesR] = await Promise.all([
        api.get("/evaluations/doc-templates" + (typeFilter ? "?doc_type=" + typeFilter : "")),
        api.get("/evaluations/doc-types"),
      ]);
      setTpls(tplsR.data || []);
      setCustomTypes(typesR.data?.custom || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const createNew = async (dtype) => {
    try {
      const r = await api.post("/evaluations/doc-templates", { title: "Nouveau " + (DOC_TYPES.find((d) => d.v === dtype)?.l || "document"), doc_type: dtype });
      navigate("/admin/formation/doc-templates/" + r.data.template_id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const createCustomType = async () => {
    if (!newTypeLabel.trim()) { toast.error("Libelle requis"); return; }
    try {
      const r = await api.post("/evaluations/doc-types", { label: newTypeLabel, description: newTypeDesc });
      toast.success("Type cree");
      setNewTypeLabel(""); setNewTypeDesc(""); setShowNewType(false);
      // Offer to open the editor with a blank template of the new type
      const tplR = await api.post("/evaluations/doc-templates", { title: "Nouveau " + r.data.label, doc_type: r.data.key });
      navigate("/admin/formation/doc-templates/" + tplR.data.template_id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const deleteCustomType = async (key) => {
    if (!window.confirm("Supprimer ce type ? Les documents existants ne seront pas impactes.")) return;
    try {
      await api.delete("/evaluations/doc-types/" + key);
      toast.success("Type supprime");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", importType);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      const r = await api.post("/evaluations/doc-templates/import-from-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Document importe (${r.data?.structure?.body?.length || 0} blocs)`);
      navigate("/admin/formation/doc-templates/" + r.data.template_id);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setImporting(false);
    e.target.value = "";
  };

  const duplicate = async (id) => {
    try {
      await api.post("/evaluations/doc-templates/" + id + "/duplicate");
      toast.success("Template duplique");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (t) => {
    if (!window.confirm("Supprimer \"" + t.title + "\" ?")) return;
    try {
      await api.delete("/evaluations/doc-templates/" + t.template_id);
      toast.success("Supprime");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? tpls.filter((t) => (t.title || "").toLowerCase().includes(q)) : tpls;
  const grouped = {};
  filtered.forEach((t) => {
    const cat = DOC_TYPES.find((d) => d.v === t.doc_type) || { l: "Autre", v: "autre", icon: FileText };
    if (!grouped[cat.v]) grouped[cat.v] = { cat, items: [] };
    grouped[cat.v].items.push(t);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="doc-templates-list">
      <PageHeader
        icon={<FileSignature />}
        title="Constructeur de documents"
        description="Contrats, conventions, études de besoins, satisfactions, attestations… avec balises dynamiques et indicateurs Qualiopi rattachés."
        breadcrumb={[{ label: "Formation" }, { label: "Documents Qualiopi" }]}
        testId="doc-templates-list-header"
      />

      {/* Quick create */}
      <div className="bg-[#0b0811] border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Creer un nouveau document</div>
          <div className="flex items-center gap-2">
            <select value={importType} onChange={(e) => setImportType(e.target.value)} className="bg-[#14101c] border border-white/10 rounded px-2 py-1 text-[10px] text-white" title="Type du document importe">
              {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select>
            <input ref={importInputRef} type="file" accept=".docx,.pdf,.html,.htm" onChange={handleImport} className="hidden" data-testid="import-doc-file" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10 h-8 text-xs"
              data-testid="import-doc-btn"
            >
              {importing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              {importing ? "Import..." : "Importer DOCX / PDF"}
            </Button>
            <Button
              size="sm"
              onClick={() => setShowNewType(true)}
              className="bg-[#f7c935] text-[#14101c] h-8 text-xs font-bold"
              data-testid="new-doc-type-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Nouveau type de document
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="doc-type-chips">
          {DOC_TYPES.map((d) => (
            <div key={d.v} className="group relative">
              <button onClick={() => createNew(d.v)} className="flex items-center gap-2 text-xs text-slate-300 bg-[#14101c] hover:bg-[#1e1a2a] hover:text-[#f7c935] border border-white/5 hover:border-[#f7c935]/30 rounded-lg px-3 py-2 transition-colors" data-testid={"doc-new-" + d.v}>
                <Plus className="w-3.5 h-3.5" />
                {d.l}
                {d.custom && <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 uppercase font-bold ml-1">custom</span>}
              </button>
              {d.custom && (
                <button onClick={() => deleteCustomType(d.v)} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-[10px]" title="Supprimer ce type">
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* New custom type modal */}
      {showNewType && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={() => setShowNewType(false)}>
          <div className="bg-[#14101c] border border-white/10 rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="new-doc-type-modal">
            <h3 className="text-lg font-bold text-white mb-1">Nouveau type de document</h3>
            <p className="text-[11px] text-slate-500 mb-4">Creez un type personnalise (audit, bilan, bordereau...) qui sera disponible pour tous les documents de votre organisme.</p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1 block">Libelle</label>
                <Input value={newTypeLabel} onChange={(e) => setNewTypeLabel(e.target.value)} placeholder="Bordereau de reglement" className="bg-[#0b0811] border-white/10 text-white" data-testid="new-doc-type-label" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1 block">Description (optionnel)</label>
                <Input value={newTypeDesc} onChange={(e) => setNewTypeDesc(e.target.value)} placeholder="Courte description..." className="bg-[#0b0811] border-white/10 text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setShowNewType(false)} className="text-slate-400 text-xs">Annuler</Button>
              <Button onClick={createCustomType} className="bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid="save-new-doc-type"><Plus className="w-3 h-3 mr-1" /> Creer et ouvrir l'editeur</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 items-center mb-4">
        <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#0b0811] border-white/10 text-white max-w-sm" data-testid="doc-search" />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white">
          <option value="">Tous les types</option>
          {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
        </select>
        <div className="text-xs text-slate-500 ml-auto">{filtered.length} template{filtered.length > 1 ? "s" : ""}</div>
      </div>

      {loading ? (
        <div className="text-center py-20"><CardSkeleton /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-xl bg-[#0b0811]">
          <FileSignature className="w-10 h-10 mx-auto text-slate-700 mb-3" />
          <p className="text-sm text-slate-500">Aucun template — choisissez un type de document ci-dessus.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.values(grouped).map(({ cat, items }) => (
            <div key={cat.v}>
              <div className="flex items-center gap-2 mb-2">
                <cat.icon className="w-4 h-4 text-[#f7c935]" />
                <h3 className="font-bold text-white text-sm">{cat.l}</h3>
                <span className="text-[10px] text-slate-500">({items.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((t) => (
                  <div key={t.template_id} className="group bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-[#f7c935]/30 transition-colors" data-testid={"doc-card-" + t.template_id}>
                    <div className="font-bold text-white text-sm truncate">{t.title}</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={"text-[9px] px-1.5 py-0.5 rounded uppercase font-bold " + (t.status === "published" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400")}>{t.status === "published" ? "Publie" : "Brouillon"}</span>
                      {t.requires_signature && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-bold uppercase">Signature</span>}
                      {(t.linked_indicators || []).length > 0 && <span className="text-[9px] text-slate-500"><Tag className="w-2.5 h-2.5 inline" /> Ind {(t.linked_indicators || []).join(", ")}</span>}
                    </div>
                    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" onClick={() => navigate("/admin/formation/doc-templates/" + t.template_id)} className="h-7 bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid={"doc-edit-" + t.template_id}>
                        <Pencil className="w-3 h-3 mr-1" /> Editer
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => duplicate(t.template_id)} className="h-7 text-slate-400 text-xs"><Copy className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(t)} className="h-7 text-red-400 text-xs"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * Editor with header / body / footer
 * ═══════════════════════════════════════════════════════════════ */
export function DocTemplateEditorPage() {
  const { template_id } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [activeSection, setActiveSection] = useState("body"); // header | body | footer
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [tags, setTags] = useState({ builtin: [], custom: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, tg] = await Promise.all([
        api.get("/evaluations/doc-templates/" + template_id),
        api.get("/lms/tags/available"),
      ]);
      setTpl(t.data);
      setTags(tg.data || { builtin: [], custom: [] });
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [template_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [template_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/evaluations/doc-templates/" + template_id, {
        title: tpl.title,
        description: tpl.description,
        structure: tpl.structure,
        linked_indicators: tpl.linked_indicators,
        requires_signature: tpl.requires_signature,
        status: tpl.status,
      });
      toast.success("Enregistre");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const blocks = tpl?.structure?.[activeSection] || [];

  const setBlocks = (bs) => {
    setTpl((t) => ({ ...t, structure: { ...(t.structure || {}), [activeSection]: bs } }));
  };

  const addBlock = (b) => {
    setBlocks([...blocks, b]);
    setSelectedIndex(blocks.length);
  };

  const updateBlock = (i, b) => {
    const bs = [...blocks];
    bs[i] = b;
    setBlocks(bs);
  };

  const moveBlock = (i, delta) => {
    const bs = [...blocks];
    const j = i + delta;
    if (j < 0 || j >= bs.length) return;
    [bs[i], bs[j]] = [bs[j], bs[i]];
    setBlocks(bs);
  };

  const deleteBlock = (i) => {
    setBlocks(blocks.filter((_, idx) => idx !== i));
    setSelectedIndex(null);
  };

  const insertTag = (key) => {
    if (selectedIndex == null) { toast.info("Selectionnez d'abord un bloc texte"); return; }
    const b = blocks[selectedIndex];
    if (!b || !["heading", "paragraph", "quote", "callout", "list"].includes(b.type)) { toast.info("Les balises s'inserent dans les blocs texte"); return; }
    const token = "{{" + key + "}}";
    const b2 = { ...b };
    if (b.type === "list") b2.items = [...(b.items || []), token];
    else b2.text = (b.text || "") + token;
    updateBlock(selectedIndex, b2);
  };

  if (loading) return <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#f7c935]" /></div>;
  if (!tpl) return <div className="p-6">Introuvable</div>;

  const theme = THEMES.light;
  const docTypeMeta = SYSTEM_TYPES.find((d) => d.v === tpl.doc_type) || { l: tpl.doc_type || "Autre", v: tpl.doc_type || "autre", icon: FileText };

  return (
    <div className="min-h-screen" data-testid="doc-editor-page">
      <div className="sticky top-0 z-20 bg-[#0b0811]/95 backdrop-blur border-b border-white/10 px-5 py-3 flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate("/admin/formation/doc-templates")} className="text-slate-400" data-testid="doc-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <input type="text" value={tpl.title || ""} onChange={(e) => setTpl((t) => ({ ...t, title: e.target.value }))} className="flex-1 bg-transparent border-0 outline-none text-xl font-black text-white" data-testid="doc-title" />
        <span className="text-[10px] px-2 py-1 rounded bg-white/5 text-slate-300 uppercase tracking-wider">{docTypeMeta?.l || tpl.doc_type}</span>
        <select value={tpl.status || "draft"} onChange={(e) => setTpl((t) => ({ ...t, status: e.target.value }))} className="bg-[#14101c] border border-white/10 rounded px-2 py-1.5 text-xs text-white">
          <option value="draft">Brouillon</option>
          <option value="published">Publie</option>
        </select>
        <Button onClick={() => setPreview((p) => !p)} variant="ghost" className="text-slate-300" data-testid="doc-preview-toggle">
          <Eye className="w-4 h-4 mr-1" /> {preview ? "Editer" : "Apercu"}
        </Button>
        <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="doc-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Enregistrer
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4 p-4">
        {!preview && (
          <aside className="col-span-12 lg:col-span-2 space-y-3">
            <BlockPalette onAdd={addBlock} api={api} />
            <TagsPanel tags={tags} onPick={insertTag} />
          </aside>
        )}

        <main className={"col-span-12 " + (preview ? "lg:col-span-12" : "lg:col-span-7")}>
          {!preview && (
            <div className="flex gap-2 mb-3" data-testid="doc-section-tabs">
              {["header", "body", "footer"].map((k) => (
                <button key={k} onClick={() => { setActiveSection(k); setSelectedIndex(null); }}
                  className={"px-4 py-2 rounded-lg text-xs font-bold transition-colors " + (activeSection === k ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] text-slate-400 hover:text-white border border-white/10")}
                  data-testid={"doc-section-" + k}>
                  {k === "header" ? "En-tete" : k === "body" ? "Corps" : "Pied de page"}
                  <span className="ml-1 opacity-60">({(tpl.structure?.[k] || []).length})</span>
                </button>
              ))}
            </div>
          )}

          <div className="mx-auto rounded-xl shadow-2xl min-h-[60vh]" style={{ background: theme.bg, maxWidth: "820px" }} data-testid="doc-canvas">
            {preview ? (
              <div className="p-10">
                {["header", "body", "footer"].map((sec) => (
                  <div key={sec} className={sec === "header" ? "border-b mb-6 pb-4" : sec === "footer" ? "border-t mt-6 pt-4 text-sm" : ""} style={{ borderColor: theme.muted + "40" }}>
                    {(tpl.structure?.[sec] || []).map((b, i) => <div key={i} className="my-3"><BlockRenderer block={b} theme={theme} /></div>)}
                  </div>
                ))}
                {(!tpl.structure || Object.values(tpl.structure).every((bs) => (bs || []).length === 0)) && <div className="text-sm italic text-center py-10" style={{ color: theme.muted }}>Document vide — editez pour ajouter du contenu.</div>}
              </div>
            ) : (
              <div className="p-6 md:p-10 space-y-2">
                <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: theme.muted }}>{activeSection === "header" ? "En-tete" : activeSection === "body" ? "Corps du document" : "Pied de page"}</div>
                {blocks.map((b, i) => (
                  <EditableBlock
                    key={i}
                    block={b}
                    onChange={(nb) => updateBlock(i, nb)}
                    onUp={() => moveBlock(i, -1)}
                    onDown={() => moveBlock(i, 1)}
                    onDelete={() => deleteBlock(i)}
                    canUp={i > 0}
                    canDown={i < blocks.length - 1}
                    theme={theme}
                    onFocusBlock={() => setSelectedIndex(i)}
                    selected={selectedIndex === i}
                  />
                ))}
                {blocks.length === 0 && <div className="text-sm italic text-center py-6" style={{ color: theme.muted }}>Section vide — ajoutez un bloc depuis le panneau de gauche.</div>}
              </div>
            )}
          </div>
        </main>

        {!preview && (
          <aside className="col-span-12 lg:col-span-3 space-y-3">
            <div className="bg-[#0b0811] border border-white/10 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Reglages</div>
              <label className="text-[10px] text-slate-500 block mb-1">Description</label>
              <Textarea value={tpl.description || ""} onChange={(e) => setTpl((t) => ({ ...t, description: e.target.value }))} rows={2} className="bg-[#14101c] border-white/10 text-white text-xs" />
              <label className="flex items-center gap-2 text-xs text-slate-300 mt-3 cursor-pointer">
                <input type="checkbox" checked={!!tpl.requires_signature} onChange={(e) => setTpl((t) => ({ ...t, requires_signature: e.target.checked }))} />
                Signature electronique requise
              </label>
              <label className="text-[10px] text-slate-500 block mb-1 mt-3">Indicateurs Qualiopi (1-32, separes par virgule)</label>
              <Input value={(tpl.linked_indicators || []).join(", ")} onChange={(e) => setTpl((t) => ({ ...t, linked_indicators: e.target.value.split(",").map((x) => x.trim()).filter(Boolean).map((x) => parseInt(x)).filter((x) => !isNaN(x)) }))} placeholder="4, 5, 11" className="bg-[#14101c] border-white/10 text-white" data-testid="doc-indicators" />
              {docTypeMeta && (docTypeMeta.ind || []).length > 0 && (
                <div className="text-[10px] mt-1 text-slate-500">Suggestion : <span className="text-[#f7c935]">{docTypeMeta.ind.join(", ")}</span></div>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

export default DocTemplatesListPage;
